import { Quill } from "@vueup/vue-quill";
import ImageResize from "quill-image-resize-vue";

const playSVG = `
<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
  <polygon points="8,5 19,12 8,19" fill="currentColor"></polygon>
</svg>`;

const pauseSVG = `
<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
  <rect x="6" y="5" width="4" height="14" fill="currentColor"></rect>
  <rect x="14" y="5" width="4" height="14" fill="currentColor"></rect>
</svg>`;

export type AudioValue = {
    src: string;
    repeticoes?: number;
    podePausar?: boolean;
    audioKey?: string;
};

const audioPlayCounts = new Map<string, number>();

function createFallbackAudioKey(src: string) {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return `${src}::${crypto.randomUUID()}`;
    }

    return `${src}::${Date.now()}::${Math.random().toString(36).slice(2)}`;
}

export function resetAudioPlayCounts(scopeKey?: string) {
    if (!scopeKey) {
        audioPlayCounts.clear();
        return;
    }

    const prefix = `${scopeKey}::`;

    for (const key of audioPlayCounts.keys()) {
        if (key.startsWith(prefix)) {
            audioPlayCounts.delete(key);
        }
    }
}

function buildAudioBlot() {
    const BlockEmbed = Quill.import("blots/block/embed") as any;

    return class AudioBlot extends BlockEmbed {
        static blotName = "audioPlayer";
        static tagName = "div";
        static className = "ql-audio-wrapper";

        static create(value: AudioValue) {
            const node = super.create() as HTMLDivElement;

            const src = String(value?.src ?? "").trim();

            const repeticoes = Number.isFinite(Number(value?.repeticoes))
                ? Math.max(0, Math.floor(Number(value?.repeticoes)))
                : 0;

            const podePausar =
                repeticoes > 0
                    ? false
                    : value?.podePausar !== false;


            const audioKey = value?.audioKey || createFallbackAudioKey(src);

            node.dataset.src = src;
            node.dataset.repeticoes = String(repeticoes);
            node.dataset.podePausar = String(podePausar);
            node.dataset.audioKey = audioKey;

            const audio = document.createElement("audio");
            audio.src = src;
            audio.preload = "metadata";
            audio.controls = false;
            audio.setAttribute("playsinline", "true");

            const ui = document.createElement("div");
            ui.className = "audio-ui";

            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "audio-play";
            btn.setAttribute("aria-label", "Play/Pause");
            btn.innerHTML = playSVG;

            const time = document.createElement("span");
            time.className = "audio-time";
            time.textContent = "00:00 / 00:00";

            const reps = document.createElement("span");
            reps.className = "audio-reps";

            ui.appendChild(btn);
            ui.appendChild(time);
            ui.appendChild(reps);

            node.appendChild(audio);
            node.appendChild(ui);

            const getPlays = () => audioPlayCounts.get(audioKey) ?? 0;

            const setPlays = (nextValue: number) => {
                audioPlayCounts.set(audioKey, nextValue);
            };

            const hasReachedLimit = () => {
                return repeticoes > 0 && getPlays() >= repeticoes;
            };

            const refreshRepetitions = () => {
                const plays = getPlays();

                reps.textContent =
                    repeticoes > 0
                        ? `${Math.min(plays, repeticoes)}/${repeticoes} repetições`
                        : "";

                btn.disabled = hasReachedLimit() && audio.paused;
            };

            let duration = 0;

            const fmt = (s: number) => {
                const m = Math.floor(s / 60).toString().padStart(2, "0");
                const sec = Math.floor(s % 60).toString().padStart(2, "0");
                return `${m}:${sec}`;
            };

            const refreshTime = () => {
                const cur = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;

                const dur =
                    Number.isFinite(duration) && duration > 0
                        ? duration
                        : Number.isFinite(audio.duration)
                            ? audio.duration
                            : 0;

                time.textContent = `${fmt(cur)} / ${fmt(dur)}`;
            };

            refreshRepetitions();

            audio.addEventListener("loadedmetadata", () => {
                duration = audio.duration || 0;
                refreshTime();
            });

            audio.addEventListener("timeupdate", refreshTime);

            audio.addEventListener("ended", () => {
                audio.currentTime = 0;
                btn.classList.remove("playing");
                btn.innerHTML = playSVG;

                refreshTime();
                refreshRepetitions();
            });

            btn.addEventListener("click", () => {
                if (btn.classList.contains("playing")) {
                    if (!podePausar) {
                        return;
                    }

                    audio.pause();
                    btn.classList.remove("playing");
                    btn.innerHTML = playSVG;
                    return;
                }

                if (hasReachedLimit()) {
                    refreshRepetitions();
                    return;
                }

                audio
                    .play()
                    .then(() => {
                        if (repeticoes > 0) {
                            setPlays(getPlays() + 1);
                        }

                        btn.classList.add("playing");

                        btn.innerHTML = podePausar
                            ? pauseSVG
                            : playSVG;

                        refreshRepetitions();
                    })
                    .catch((err) => {
                        console.warn("Audio play falhou:", err);
                    });
            });

            audio.addEventListener("pause", () => {
                btn.classList.remove("playing");
                btn.innerHTML = playSVG;
                refreshRepetitions();
            });

            return node;
        }

        static value(node: HTMLElement): AudioValue {
            const repeticoes =
                Number.parseInt(node.dataset.repeticoes || "0", 10) || 0;

            return {
                src: node.dataset.src || "",
                repeticoes,
                podePausar:
                    repeticoes > 0
                        ? false
                        : node.dataset.podePausar !== "false",
                audioKey: node.dataset.audioKey || "",
            };
        }
    };
}

export function registerAudioBlot() {
    const anyQuill = Quill as any;

    if (anyQuill.__audioBlotRegistered) return;

    anyQuill.__audioBlotRegistered = true;

    const AudioBlot = buildAudioBlot();
    Quill.register(AudioBlot, true);
}

export function registerMilageFormats() {
    const anyQuill = Quill as any;

    if (anyQuill.__milageFormatsRegistered) return;

    anyQuill.__milageFormatsRegistered = true;

    const Parchment = Quill.import("parchment") as any;

    const StyleAttributor = Parchment?.Attributor?.Style;
    const AttributeAttributor = Parchment?.Attributor?.Attribute;

    if (!StyleAttributor || !AttributeAttributor) {
        console.warn("[Milage] Parchment Attributors not found.");
        return;
    }

    const BorderStyle = new StyleAttributor("border", "border", {
        scope: Parchment.Scope.INLINE,
    });

    const RadiusStyle = new StyleAttributor("br", "border-radius", {
        scope: Parchment.Scope.INLINE,
    });

    const HiddenAttributor = new AttributeAttributor("milage", "data-milage", {
        scope: Parchment.Scope.INLINE,
    });

    const MilageKeyAttributor = new AttributeAttributor("key", "data-milage-key", {
        scope: Parchment.Scope.INLINE,
    });

    const MilageIdAttributor = new AttributeAttributor("id", "data-milage-id", {
        scope: Parchment.Scope.INLINE,
    });

    const MilageId2Attributor = new AttributeAttributor(
        "milage_id",
        "data-milage-id-2",
        {
            scope: Parchment.Scope.INLINE,
        },
    );

    Quill.register(HiddenAttributor, true);
    Quill.register(MilageKeyAttributor, true);
    Quill.register(MilageIdAttributor, true);
    Quill.register(MilageId2Attributor, true);

    Quill.register(BorderStyle, true);
    Quill.register(RadiusStyle, true);

    const ResizeModule = (ImageResize as any)?.default ?? ImageResize;
    Quill.register("modules/imageResize", ResizeModule);

    registerAudioBlot();
}